// Signature WAF drawer body — section cards using shared v3 drawer UI.

import React from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";
import {
  Shield,
  Clock,
  Globe,
  Flame,
  ScanSearch,
  Activity,
  Eye,
  Target,
} from "lucide-react";

import { DETECTION_ICONS } from "../../../../../icons/detectionLucideIcons";
import colors from "../../../../../utils/colors";
import { formatDate } from "../../../../../utils/format";
import { formatWafAttackTypeLabel } from "../wafEventListUtils";
import {
  signatureViewToFilterEvent,
  signatureViewToNetworkShape,
} from "../buildSignatureWafDrawerView";
import {
  EventAssessGrid,
  SmartParseSectionCard,
  spSecondaryBodySx,
} from "./smartParseDrawerUi";
import {
  PRIMARY_COLOR,
  TEXT_PRIMARY,
  BLOCKED_STATUS_FG,
  drawerSeverityPalette,
  isBlockedAction,
} from "../wafEventDrawerShared";
import NetworkTargetPanel from "./NetworkTargetPanel";
import SmartParseRawJsonViewer from "./SmartParseRawJsonViewer";
import ViewSimilarEventsPanel from "./ViewSimilarEventsPanel";

function verdictValueColor(verdict) {
  const key = String(verdict || "").toLowerCase();
  if (key === "malicious") return colors.red[200];
  if (key === "suspicious") return "#b45309";
  return TEXT_PRIMARY;
}

export default function SignatureWafEventDrawerBody({ view, onCopy, onFilterSimilar }) {
  if (!view) return null;

  const ds = view.detectionSummary;
  const md = view.matchDetails;
  const { attacker, target } = signatureViewToNetworkShape(view);
  const filterEvent = signatureViewToFilterEvent(view);

  const hasMatchDetails =
    md.messages.length > 0 || md.fields.length > 0 || md.values.length > 0;

  const riskPalette = drawerSeverityPalette(ds.riskLevel);
  const actionColor = isBlockedAction(ds.action) ? BLOCKED_STATUS_FG : TEXT_PRIMARY;

  const summaryCells = [
    {
      key: "timestamp",
      label: "Timestamp",
      value: ds.timestamp ? formatDate(ds.timestamp) : null,
      icon: Clock,
    },
    {
      key: "hostname",
      label: "Hostname",
      value: ds.hostname,
      icon: Globe,
      valueColor: PRIMARY_COLOR,
    },
    {
      key: "verdict",
      label: "Verdict",
      value: ds.verdict,
      icon: ScanSearch,
      valueColor: verdictValueColor(ds.verdict),
    },
    {
      key: "riskLevel",
      label: "Risk Level",
      value: ds.riskLevel,
      icon: Flame,
      valueColor: riskPalette.fg,
    },
    {
      key: "action",
      label: "Action",
      value: ds.action,
      icon: Shield,
      valueColor: actionColor,
    },
    {
      key: "source",
      label: "Source",
      value: ds.wafType,
      icon: DETECTION_ICONS.wafDetection,
    },
    ds.matchedRuleId != null && ds.matchedRuleId !== ""
      ? {
        key: "ruleId",
        label: "Rule ID",
        value: String(ds.matchedRuleId),
        icon: Target,
        monospace: true,
      }
      : null,
    ds.anomalyScore != null
      ? {
        key: "anomalyScore",
        label: "Anomaly Score",
        value: String(ds.anomalyScore),
        icon: Activity,
      }
      : null,
    ds.paranoiaLevel != null
      ? {
        key: "paranoiaLevel",
        label: "Paranoia Level",
        value: String(ds.paranoiaLevel),
        icon: Eye,
      }
      : null,
  ].filter(Boolean);

  return (
    <>
      <SmartParseSectionCard icon={DETECTION_ICONS.clipboard} title="Assessment">
        <EventAssessGrid cells={summaryCells} />
      </SmartParseSectionCard>

      {hasMatchDetails && (
        <SmartParseSectionCard icon={Shield} title="Match Details">
          <EventAssessGrid
            cells={[
              md.messages.length > 0
                ? {
                  key: "messages",
                  label: "Match messages",
                  value: md.messages.join(", "),
                  icon: DETECTION_ICONS.contentNegotiation,
                }
                : null,
              md.fields.length > 0
                ? {
                  key: "fields",
                  label: "Match fields",
                  value: md.fields.join(", "),
                  icon: DETECTION_ICONS.header,
                  monospace: true,
                }
                : null,
              md.values.length > 0
                ? {
                  key: "values",
                  label: "Match values",
                  value: md.values.join(", "),
                  icon: DETECTION_ICONS.uri,
                  monospace: true,
                  copyable: true,
                }
                : null,
            ].filter(Boolean)}
            onCopy={onCopy}
          />
        </SmartParseSectionCard>
      )}

      {view.attackTypes.length > 0 && (
        <SmartParseSectionCard icon={DETECTION_ICONS.detection} title="Attack Types">
          <Typography sx={spSecondaryBodySx}>
            {view.attackTypes.map((t) => formatWafAttackTypeLabel(t) || t).join(" · ")}
          </Typography>
        </SmartParseSectionCard>
      )}

      <SmartParseSectionCard icon={DETECTION_ICONS.ip} title="Network & Target">
        <NetworkTargetPanel attacker={attacker} target={target} onCopy={onCopy} />
      </SmartParseSectionCard>

      <SmartParseSectionCard icon={DETECTION_ICONS.info} title="Raw Event" defaultExpanded={false}>
        <SmartParseRawJsonViewer
          data={view.rawEventFiltered}
          onCopy={onCopy}
          filename="signature_waf_event.json"
        />
      </SmartParseSectionCard>

      <ViewSimilarEventsPanel event={filterEvent} onFilterSimilar={onFilterSimilar} />
    </>
  );
}

SignatureWafEventDrawerBody.propTypes = {
  view: PropTypes.object,
  onCopy: PropTypes.func,
  onFilterSimilar: PropTypes.func,
};
